import type { ReactNode } from 'react';
import {
  // BookmarkSimpleIcon, // MED-6966: not wired on the BE yet, hide everywhere
  HeadphonesIcon,
  HighlighterIcon,
  LightningIcon,
  ListChecksIcon,
  PlayIcon,
  SpeakerHighIcon,
  ArticleIcon,
} from '@phosphor-icons/react';
import type { KnowledgeHubTabItem, TabBadgeStatus } from '@libertify/ui';
import type { AddOnType } from './types';

/**
 * Knowledge Hub tab keys (see each app's ExperiencePage `tabs`).
 *
 * NOTE: the `flashcards` tab key displays as "Quick recall". The frontend
 * identifiers were renamed to QuickRecall (MED-6933), but this key is left as
 * `flashcards` deliberately: it doubles as the `AddonTabOpen` analytics value
 * (see ADDON_KEYS in ./types) and maps to the backend's `Flashcards` add-on
 * type, so renaming it would break analytics continuity and the add-on wiring.
 *
 * `audioDoc` and `podcast` used to be a single shared `audio` tab (MED-7075
 * split them into their own cards — see ADDON_TYPE_TO_TAB below). `audioDoc`
 * stays a real `TabKey` (status/toast tracking treats it like any other
 * add-on, per MED-7075) but is deliberately absent from `TAB_ORDER`: some
 * legacy projects/playbooks already have `AudioDoc` in their `enabled_add_ons`
 * from before it was hidden from admin's create/edit flow (MED-7032), and
 * without this it would still surface its own tab for those. Same pattern as
 * `bookmarks` below.
 */
export type TabKey =
  | 'video'
  | 'audioDoc'
  | 'podcast'
  | 'summary'
  | 'quiz'
  // | 'bookmarks' // MED-6966: not wired on the BE yet, hide everywhere
  | 'flashcards'
  | 'standouts';

/**
 * Canonical order of the Knowledge Hub tabs — the fallback for tabs the
 * experience's `enabled_add_ons` doesn't rank (see `orderTabs`).
 *
 * `audioDoc` is deliberately NOT here — see the `TabKey` doc above. It stays
 * in `ADDON_TYPE_TO_TAB`/`TAB_ICON` so status + toast tracking still treats
 * it like any other add-on; `orderTabs` bounds every surface's strip to this
 * list, so a tab absent here can never render regardless of what's enabled.
 */
export const TAB_ORDER: TabKey[] = [
  'video',
  'podcast',
  'summary',
  'quiz',
  // 'bookmarks', // MED-6966: not wired on the BE yet, hide everywhere
  'flashcards',
  'standouts',
];

/**
 * Which tab an add-on renders in. Add-ons absent here (Bookmarks/Trivia/
 * Chatbot) have no tab — their status is tracked but never toasted or shown.
 *
 * `Video` is intentionally NOT here: the video is the project's MEDIA, tracked
 * via the `MediaStatusChanged` event (see admin's `useMediaStatus`), not the
 * add-ons pipeline. Adding it would let a `Video` `AddOnStatusChanged` event
 * raise a second video toast on top of the media one. Video *visibility* is
 * resolved from `enabled_add_ons` like every other add-on's — see `addOnTab`.
 */
export const ADDON_TYPE_TO_TAB: Partial<Record<AddOnType, TabKey>> = {
  AudioDoc: 'audioDoc',
  Podcast: 'podcast',
  Summary: 'summary',
  Quiz: 'quiz',
  // Bookmarks: 'bookmarks', // MED-6966: not wired on the BE yet, hide everywhere
  Flashcards: 'flashcards',
  Standouts: 'standouts',
};

export interface ResolveVisibleTabsArgs {
  /**
   * Add-ons available on this experience. Each surface supplies the source it
   * can actually see:
   *  - admin/sales (authed): the union of the project's `enabled_add_ons`
   *    config and the add-on states on `storyboards[].add_ons[]`, so an
   *    enabled-but-not-yet-generated add-on still gets its tab (with a loading
   *    state) and a legacy project with an empty config still shows the
   *    add-ons it actually has.
   *  - media-player (public): the add-ons whose asset is published on the
   *    instance — an ungenerated add-on has no tab, which is what an end
   *    viewer should see.
   */
  availableAddOns: AddOnType[];
}

/** The tab an add-on renders in. `Video` is resolved here, not in
 *  `ADDON_TYPE_TO_TAB` — see the note there. */
const addOnTab = (addOn: AddOnType): TabKey | undefined =>
  addOn === 'Video' ? 'video' : ADDON_TYPE_TO_TAB[addOn];

export interface OrderTabsArgs {
  tabs: TabKey[];
  /** The experience's `enabled_add_ons`, whose sequence is the arrangement
   *  saved in the Edit Experience rail. */
  arrangement: AddOnType[];
}

/**
 * Order the tabs by that saved arrangement (MED-7102). Tabs it doesn't name
 * fall back to their `TAB_ORDER` slot, ahead of the arranged ones — chiefly a
 * legacy project's video.
 *
 * Also the one place the strip is bounded to `TAB_ORDER`, so `audioDoc` can
 * never render whatever the arrangement says.
 */
export const orderTabs = ({ tabs, arrangement }: OrderTabsArgs): TabKey[] => {
  const visible = new Set(tabs.filter((tab) => TAB_ORDER.includes(tab)));
  const arranged: TabKey[] = [];
  for (const addOn of arrangement) {
    const tab = addOnTab(addOn);
    if (tab && visible.has(tab) && !arranged.includes(tab)) arranged.push(tab);
  }
  const unarranged = TAB_ORDER.filter(
    (tab) => visible.has(tab) && !arranged.includes(tab),
  );
  return [...unarranged, ...arranged];
};

/**
 * The Knowledge Hub tabs an experience should render, in the creator's saved
 * arrangement — `availableAddOns` doubles as it (see `orderTabs`).
 *
 * A tab is visible when at least one available add-on maps to it AND it's in
 * `TAB_ORDER` — `audioDoc` maps from `AudioDoc` but isn't in that list, so it
 * never surfaces here even for a project with it enabled (see `TAB_ORDER`).
 * `video` is no exception: it shows only when `Video` is available. It used to
 * also show whenever the experience had a playable video, which made hiding the
 * Video add-on a no-op on every experience that had one (MED-7123).
 *
 * An empty result is meaningful — the caller hides the whole panel. Callers
 * must therefore only trust it once their availability sources have RESOLVED;
 * an in-flight fetch looks identical to "nothing is enabled" from here.
 */
export const resolveVisibleTabs = ({
  availableAddOns,
}: ResolveVisibleTabsArgs): TabKey[] => {
  const visible = new Set<TabKey>();
  for (const addOn of availableAddOns) {
    const tab = addOnTab(addOn);
    if (tab) visible.add(tab);
  }
  return orderTabs({ tabs: [...visible], arrangement: availableAddOns });
};

/** Icon shown above each tab's label. Identical across every surface. */
const TAB_ICON: Record<TabKey, ReactNode> = {
  video: <PlayIcon size={18} />,
  audioDoc: <SpeakerHighIcon size={18} />,
  podcast: <HeadphonesIcon size={18} />,
  summary: <ArticleIcon size={18} />,
  quiz: <ListChecksIcon size={18} />,
  // bookmarks: <BookmarkSimpleIcon size={18} />, // MED-6966: not wired on the BE yet, hide everywhere
  flashcards: <LightningIcon size={18} />,
  standouts: <HighlighterIcon size={18} />,
};

/** i18n key for a tab's label, resolved by the consuming app's translator. */
export const getKnowledgeHubTabLabelKey = (tab: TabKey): string =>
  `experienceV2.knowledgeHub.tabs.${tab}`;

export interface BuildKnowledgeHubTabsArgs {
  /** Tabs to render, in order — usually `resolveVisibleTabs`' result. */
  visibleTabs: TabKey[];
  /**
   * Rendered add-on per tab. A tab with no entry is dropped, so a surface can
   * simply omit an add-on it doesn't implement rather than filtering it out
   * separately.
   */
  content: Partial<Record<TabKey, ReactNode>>;
  /** The app's translator — Refine's `useTranslate` in admin, i18next elsewhere. */
  translate: (key: string) => string;
  /**
   * Status indicator per tab (generating, or ready-but-unread). Omitted
   * entirely by a surface that doesn't track it — a tab then renders with no
   * badge, same as today.
   */
  badgeStatuses?: Partial<Record<TabKey, TabBadgeStatus>>;
}

/**
 * Build the `<KnowledgeHubPanel tabs>` list: visibility (from `visibleTabs`)
 * combined with the shared per-tab icon + label, and the caller's content.
 *
 * Lives here so the three surfaces don't each repeat the same six icons and
 * label keys — they differ only in how they build `content` and how they work
 * out which tabs exist. Mirrors `callToActionPresentation` for the CTA row.
 */
export const buildKnowledgeHubTabs = ({
  visibleTabs,
  content,
  translate,
  badgeStatuses,
}: BuildKnowledgeHubTabsArgs): KnowledgeHubTabItem[] =>
  visibleTabs
    .filter((tab) => content[tab] !== undefined)
    .map((tab) => ({
      key: tab,
      label: translate(getKnowledgeHubTabLabelKey(tab)),
      icon: TAB_ICON[tab],
      content: content[tab],
      badgeStatus: badgeStatuses?.[tab],
    }));
